import _ from 'lodash'
import { defineStore } from 'pinia'
import { gql } from '~/tada/gql'

export type MamKind = 'image' | 'video'

/** A Typesense hit from DiffusionDB (images) or VideoDiffusionDB (video moments). */
export type MamHit = {
  id: string
  asset_id: number
  asset_type: string
  file_name: string
  film_festival_id: number
  film_name: string
  team_id: number
  team_name: string
  aspect_ratio?: string
  mux_playback_id?: string
  time?: number
}

export type MamItem = {
  key: string
  kind: MamKind
  assetId: number
  fileName: string
  assetType: string
  teamId: number
  teamName: string
  filmName: string
  festivalId: number
  muxPlaybackId?: string
  /** Seconds into the video where the collected moment was found. */
  time?: number
  /** Bytes; undefined until probed, null when Spaces would not say. */
  size?: number | null
  lastModified?: number
}

export type MamJob = {
  id: string
  filename: string
  status: 'preparing' | 'downloading' | 'done' | 'error' | 'cancelled'
  written: number
  total: number | null
  file: number
  files: number
  message?: string
}

export const MAM_PATH = '/admin/mam'

export const MAM_TABS = {
  images: {
    kind: 'image',
    title: 'Images',
    icon: 'mdi-image-outline',
    index: 'DiffusionDB',
    facets: [
      { title: 'Team Name', attribute: 'team_name' },
      { title: 'Film Name', attribute: 'film_name' },
      { title: 'Asset Type', attribute: 'asset_type' },
      { title: 'Film Festival', attribute: 'film_festival_id' },
    ],
  },
  videos: {
    kind: 'video',
    title: 'Videos',
    icon: 'mdi-video-outline',
    index: 'VideoDiffusionDB',
    facets: [
      { title: 'Team Name', attribute: 'team_name' },
      { title: 'Film Name', attribute: 'film_name' },
      { title: 'Aspect Ratio', attribute: 'aspect_ratio' },
      { title: 'Asset Type', attribute: 'asset_type' },
      { title: 'Film Festival', attribute: 'film_festival_id' },
    ],
  },
} as const

export type MamTab = keyof typeof MAM_TABS

const MAM_INDEX: Record<MamKind, string> = {
  image: MAM_TABS.images.index,
  video: MAM_TABS.videos.index,
}

/** Uploads are stored as `<uuid>-<original name>`. */
export const originalName = (fileName: string) =>
  fileName.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, '')

export const mamItemKey = (kind: MamKind, assetId: number) => `${kind}:${assetId}`

export const toMamItem = (kind: MamKind, hit: MamHit): MamItem => ({
  key: mamItemKey(kind, hit.asset_id),
  kind,
  assetId: hit.asset_id,
  fileName: hit.file_name,
  assetType: hit.asset_type,
  teamId: hit.team_id,
  teamName: hit.team_name,
  filmName: hit.film_name,
  festivalId: hit.film_festival_id,
  muxPlaybackId: hit.mux_playback_id,
  time: kind === 'video' ? hit.time : undefined,
})

export const mamThumb = (
  item: { kind: MamKind; fileName: string; muxPlaybackId?: string; time?: number },
  size = 300,
) =>
  item.kind === 'image'
    ? $imgr(item.fileName, `rs:fill:${size}:${size}`)
    : `https://image.mux.com/${item.muxPlaybackId}/thumbnail.png?height=${size}&width=${size}&fit_mode=smartcrop${item.time != null ? `&time=${item.time}` : ''}`

export const formatBytes = (bytes?: number | null) => {
  if (bytes == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let n = bytes
  let u = 0
  while (n >= 1000 && u < units.length - 1) {
    n /= 1000
    u++
  }
  return `${n.toFixed(u && n < 10 ? 1 : 0)} ${units[u]}`
}

export const formatTimecode = (seconds?: number) => {
  if (seconds == null) return ''
  const s = Math.floor(seconds)
  const hh = Math.floor(s / 3600)
  const mm = String(Math.floor((s % 3600) / 60)).padStart(hh ? 2 : 1, '0')
  const ss = String(s % 60).padStart(2, '0')
  return hh ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`
}

const SignMamAsset = gql(`mutation SignMamAsset($name: String!) {
  assetSign(name: $name)
}`)

const PROBE_CONCURRENCY = 6

/** Path segments safe on macOS and Windows: no separators, reserved or control characters. */
const safeSegment = (s: string) =>
  [...s]
    .map(c => (c < ' ' || '\\/:*?"<>|'.includes(c) ? '_' : c))
    .join('')
    .replace(/\s+/g, ' ')
    .trim() || 'Untitled'

const csvCell = (value: unknown) => {
  const s = value == null ? '' : String(value)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Zip layout: one folder per film, original file names, de-duplicated. */
const zipEntries = (items: MamItem[]) => {
  const used = new Set<string>()
  const files = items.map(item => {
    const folder = safeSegment(`${item.teamName} - ${item.filmName}`)
    const name = safeSegment(originalName(item.fileName))
    const dot = name.lastIndexOf('.')
    const [stem, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, '']
    let path = `${folder}/${name}`
    for (let n = 2; used.has(path.toLowerCase()); n++) path = `${folder}/${stem} (${n})${ext}`
    used.add(path.toLowerCase())
    return { item, path }
  })

  const manifest = [
    ['path', 'kind', 'asset_type', 'team', 'film', 'festival_year', 'moment', 'bytes', 'asset_id', 'file_name'],
    ...files.map(({ item, path }) => [
      path,
      item.kind,
      item.assetType,
      item.teamName,
      item.filmName,
      item.festivalId + 2000,
      formatTimecode(item.time),
      item.size ?? '',
      item.assetId,
      item.fileName,
    ]),
  ]
    .map(row => row.map(csvCell).join(','))
    .join('\r\n')

  return [
    ...files.map(({ item, path }) => ({
      path,
      fileName: item.fileName,
      size: item.size ?? null,
      lastModified: item.lastModified,
    })),
    { path: 'manifest.csv', text: '﻿' + manifest + '\r\n' },
  ]
}

/** The service worker with the zip add-on, or null if it isn't running. */
const zipWorker = async (): Promise<ServiceWorker | null> => {
  if (!('serviceWorker' in navigator)) return null
  const registration = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>(r => setTimeout(() => r(null), 3000)),
  ])
  const worker = registration?.active
  if (!worker) return null
  const channel = new MessageChannel()
  const hello = new Promise<boolean>(resolve => {
    channel.port1.onmessage = e => resolve(e.data?.type === 'hello')
    setTimeout(() => resolve(false), 2000)
  })
  worker.postMessage({ type: 'mam-zip:hello' }, [channel.port2])
  const ok = await hello
  channel.port1.close()
  return ok ? worker : null
}

/**
 * Clips and images an editor has gathered from the MAM, kept in localStorage
 * so the collection survives reloads, and downloaded as one zip that the
 * service worker streams straight from Spaces.
 */
export const useMamCollection = defineStore('mamCollection', () => {
  const stored = useLocalStorage<MamItem[]>('mam:collection', [])
  const zipName = useLocalStorage('mam:zip-name', '')
  const drawer = useLocalStorage('mam:drawer', true)

  const items = computed(() => stored.value)
  const keys = computed(() => new Set(stored.value.map(i => i.key)))
  const has = (key: string) => keys.value.has(key)

  const totalSize = computed(() =>
    stored.value.every(i => i.size != null) ? _.sumBy(stored.value, i => i.size!) : null,
  )
  const knownSize = computed(() => _.sumBy(stored.value, i => i.size ?? 0))

  const { mutate: sign } = useMutation(SignMamAsset)
  const signUrl = async (fileName: string) => {
    const res = await sign({ name: fileName })
    const url = res?.data?.assetSign
    if (!url) throw new Error(`Unable to sign ${fileName}`)
    return url
  }

  // Sizes come from the headers of a signed GET, a few at a time. Spaces'
  // CORS rules don't allow HEAD, so the body is abandoned once headers arrive.
  const probing = ref(new Set<string>())
  const probe = async (item: MamItem) => {
    const abort = new AbortController()
    try {
      const res = await fetch(await signUrl(item.fileName), {
        cache: 'no-store',
        signal: abort.signal,
      })
      const length = Number(res.headers.get('content-length'))
      const modified = Date.parse(res.headers.get('last-modified') ?? '')
      return {
        size: res.ok && res.headers.has('content-length') && Number.isFinite(length) ? length : null,
        lastModified: Number.isNaN(modified) ? undefined : modified,
      }
    } catch {
      return { size: null }
    } finally {
      abort.abort()
    }
  }
  /** Measures unmeasured items, and with `retry` those that failed before. */
  const probeMissing = async (retry = false) => {
    const queue = stored.value.filter(
      i => (i.size === undefined || (retry && i.size === null)) && !probing.value.has(i.key),
    )
    queue.forEach(i => probing.value.add(i.key))
    const work = async () => {
      for (let item = queue.shift(); item; item = queue.shift()) {
        const result = await probe(item)
        probing.value.delete(item.key)
        const current = stored.value.find(i => i.key === item!.key)
        if (current) Object.assign(current, result)
      }
    }
    await Promise.all(_.times(PROBE_CONCURRENCY, work))
  }
  const sizing = computed(() => probing.value.size > 0)

  const add = (newItems: MamItem[]) => {
    const fresh = _.uniqBy(newItems, 'key').filter(i => !has(i.key))
    if (!fresh.length) return 0
    stored.value = [...stored.value, ...fresh]
    probeMissing()
    return fresh.length
  }
  const remove = (removeKeys: string[]) => {
    const drop = new Set(removeKeys)
    stored.value = stored.value.filter(i => !drop.has(i.key))
  }
  const toggle = (item: MamItem) => (has(item.key) ? remove([item.key]) : add([item]))
  const clear = () => (stored.value = [])

  /** Every image or video a team uploaded, one item per asset. */
  const teamItems = async (kind: MamKind, teamId: number) => {
    const { $typesense } = useNuxtApp()
    const documents = $typesense.collections<MamHit>(MAM_INDEX[kind]).documents()
    const found: MamItem[] = []
    for (let page = 1; ; page++) {
      const res = await documents.search({
        q: '*',
        filter_by: `team_id:=${teamId}`,
        exclude_fields: 'embedding',
        per_page: 250,
        page,
        ...(kind === 'video' ? { group_by: 'asset_id', group_limit: 1 } : {}),
      })
      const hits =
        kind === 'video'
          ? (res.grouped_hits ?? []).map(g => g.hits[0]?.document)
          : (res.hits ?? []).map(h => h.document)
      // A team-wide pick isn't tied to any one moment of a video.
      found.push(...hits.filter(Boolean).map(h => ({ ...toMamItem(kind, h!), time: undefined })))
      if (hits.length < 250) return found
    }
  }
  const addTeam = async (kind: MamKind, teamId: number) => add(await teamItems(kind, teamId))

  // Anything collected before sizes were known (or before a reload) gets probed.
  onNuxtReady(() => probeMissing(true))

  const defaultZipName = () =>
    `SATO48 MAM ${new Date().toISOString().slice(0, 16).replace('T', ' ').replace(':', '.')}`

  const job = ref<MamJob | null>(null)
  const active = computed(
    () => job.value?.status === 'preparing' || job.value?.status === 'downloading',
  )
  let port: MessagePort | null = null
  let cleanup = () => {}

  const download = async () => {
    if (active.value || !stored.value.length) return
    const id = crypto.randomUUID()
    const filename = `${safeSegment(zipName.value || defaultZipName())}.zip`
    job.value = {
      id,
      filename,
      status: 'preparing',
      written: 0,
      total: null,
      file: 0,
      files: stored.value.length + 1,
    }
    const fail = (message: string) => {
      if (job.value?.id === id) Object.assign(job.value, { status: 'error', message })
      cleanup()
    }

    await probeMissing(true)
    const worker = await zipWorker()
    if (!worker) return fail('The download helper is not running. Reload the page and try again.')

    const channel = new MessageChannel()
    port = channel.port1
    const iframe = document.createElement('iframe')
    iframe.hidden = true
    const ping = setInterval(() => worker.postMessage({ type: 'mam-zip:ping' }), 10_000)
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    cleanup = () => {
      clearInterval(ping)
      window.removeEventListener('beforeunload', warn)
      // Leave the iframe a moment so the browser has taken the download.
      setTimeout(() => iframe.remove(), 5000)
      port?.close()
      port = null
      cleanup = () => {}
    }

    channel.port1.onmessage = async ({ data }) => {
      const current = job.value
      if (current?.id !== id) return
      switch (data.type) {
        case 'ready':
          current.total = data.total
          current.status = 'downloading'
          iframe.src = `/__/mam-zip/${id}/${encodeURIComponent(filename)}`
          document.body.appendChild(iframe)
          break
        case 'sign':
          try {
            channel.port1.postMessage({ id: data.id, url: await signUrl(data.fileName) })
          } catch (e) {
            channel.port1.postMessage({ id: data.id, error: String(e) })
          }
          break
        case 'progress':
        case 'done':
        case 'cancelled':
          Object.assign(current, {
            written: data.written,
            total: data.total,
            file: data.file,
            files: data.files,
            status: data.type === 'progress' ? 'downloading' : data.type,
          })
          if (data.type !== 'progress') cleanup()
          break
        case 'error':
          fail(data.message)
          break
      }
    }

    worker.postMessage(
      { type: 'mam-zip:create', job: { id, filename, entries: zipEntries(stored.value) } },
      [channel.port2],
    )
  }

  const cancel = () => {
    port?.postMessage({ type: 'cancel' })
    if (job.value && active.value) job.value.status = 'cancelled'
    cleanup()
  }
  const dismiss = () => {
    if (!active.value) job.value = null
  }

  return {
    items,
    has,
    add,
    remove,
    toggle,
    clear,
    addTeam,
    totalSize,
    knownSize,
    sizing,
    zipName,
    defaultZipName,
    drawer,
    job,
    active,
    download,
    cancel,
    dismiss,
  }
})
